import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Link, Stack, Typography,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import PayrollService, { VolumeImportSummary, describeError } from '../../api/services/payroll.service';
import { count, money, shortDate } from './payrollUI';
import { toCsv, downloadCsv } from '../../utils/csv';

// Bringing the hand-kept "#PKG vs Stops" sheet in, once. Every week after
// that fills itself from the statement upload.
//
// The check step matters more than usual: weeks that are in the sheet AND
// have an uploaded statement are compared, which is how anyone can see
// that this page counts the way the sheet did.

// One row a week. Totals may be left blank — they are the three kinds
// added up. The column names are the ones the importer already reads, and
// the family's own wide sheet (one block of columns per year) still works.
const TEMPLATE_HEADERS = [
  'Date', 'Pick-up #PKG', 'Pick-up Stops', 'Delivery #PKG', 'Delivery Stops',
  'E-Com #PKG', 'E-Com Stops', 'TOTAL #PKG', 'TOTAL Stops', 'Revenue',
];
const TEMPLATE_EXAMPLE = [
  ['01/09/2026', 1777, 213, 3964, 1374, 11377, 9500, '', '', '40016.22'],
  ['01/16/2026', 1667, 187, 3759, 1327, 8661, 7395, '', '', '34242.11'],
];

const FIELD = { totalPackages: 'packages', totalStops: 'stops', revenue: 'revenue' } as const;

const VolumeImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}> = ({ open, onClose, onImported }) => {
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [summary, setSummary] = useState<VolumeImportSummary | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setText(''); setFileName(''); setSummary(null); setDone(false); setBusy(false); setError('');
    if (input.current) input.current.value = '';
  };
  const close = () => {
    if (busy) return;
    if (done) onImported();
    reset();
    onClose();
  };

  const send = async (body: string, dryRun: boolean) => {
    setBusy(true);
    setError('');
    try {
      const res = await PayrollService.importVolume(body, dryRun);
      setSummary(res.summary);
      if (!dryRun) setDone(true);
    } catch (err) {
      setError(describeError(err, 'That file could not be read.'));
      setSummary(null);
    } finally {
      setBusy(false);
    }
  };

  const choose = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = await file.text();
    setText(body);
    setFileName(file.name);
    setDone(false);
    send(body, true);
  };

  const fmt = (field: keyof typeof FIELD, n: number) => (field === 'revenue' ? money(n, { cents: true }) : count(n));

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Import past weeks</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!fileName && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Weeks before your first statement upload. One row a week; totals can stay blank.
            </Typography>
            <Link
              component="button" type="button" sx={{ mb: 3, display: 'inline-block' }}
              onClick={() => downloadCsv('toposds-weekly-volume-template.csv', toCsv(TEMPLATE_HEADERS, TEMPLATE_EXAMPLE))}
            >
              Download the template
            </Link>
            <Box />
            <Button variant="contained" disableElevation component="label" startIcon={<UploadIcon />}>
              Choose CSV file
              <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={choose} />
            </Button>
          </Box>
        )}

        {fileName && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" gutterBottom><strong>{fileName}</strong></Typography>
            {busy && <LinearProgress sx={{ my: 2 }} />}

            {summary && (
              <Stack gap={2} sx={{ mt: 1 }}>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip size="small" color="success" label={`${summary.weeks} weeks`} />
                  {Object.entries(summary.byYear).sort(([a], [b]) => Number(b) - Number(a)).map(([year, n]) => (
                    <Chip key={year} size="small" variant="outlined" label={`${year}: ${n}`} />
                  ))}
                </Stack>

                {summary.corrections.length > 0 && (
                  <Alert severity="info">
                    {summary.corrections.map((c) => (
                      <div key={c.from}>&ldquo;{c.from}&rdquo; sits under {c.year} — read as {shortDate(c.to)}, {c.year}.</div>
                    ))}
                  </Alert>
                )}

                {summary.overlaps > 0 && (
                  summary.disagreements.length === 0 ? (
                    <Alert severity="success">
                      The {summary.overlaps} week{summary.overlaps === 1 ? '' : 's'} that also have a statement match it exactly.
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        {summary.agreeing} of {summary.overlaps} weeks match the uploaded statement
                      </Typography>
                      {summary.disagreements.slice(0, 8).map((d) => (
                        <Typography key={d.weekEnding} variant="body2">
                          {shortDate(d.weekEnding)}: {d.differences.map((x) => `${FIELD[x.field]} ${fmt(x.field, x.sheet)} in the sheet, ${fmt(x.field, x.statement)} on the statement`).join('; ')}
                        </Typography>
                      ))}
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        Those weeks show the statement&rsquo;s figures.
                      </Typography>
                    </Alert>
                  )
                )}

                {done && <Alert severity="success">Imported.</Alert>}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy}>{done ? 'Close' : 'Cancel'}</Button>
        {summary && !done && (
          <Button variant="contained" disableElevation disabled={busy} onClick={() => send(text, false)}>
            Import {summary.weeks} weeks
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VolumeImportDialog;
